import React, { useState } from 'react'
import { formatIssueDate } from '../lib/digest'
import { supabase } from '../lib/supabase'

export default function PublishModal({ issue, subject, html, onClose, onSent }) {
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState(null)
  const [scheduledFor, setScheduledFor] = useState('')
  const isScheduled = issue.status === 'scheduled' && issue.scheduled_for

  function formatScheduledTime(isoString) {
    const d = new Date(isoString)
    return d.toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
    })
  }

  async function send(test) {
    setSending(true)
    setResult(null)
    try {
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, html, test })
      })
      const data = await res.json()
      if (data.error) {
        setResult({ error: data.error })
      } else {
        setResult({ sent: data.sent, test })
        if (!test) {
          await supabase.from('issues').update({ status: 'ready' }).eq('id', issue.id)
          setTimeout(onSent, 1500)
        }
      }
    } catch (e) {
      setResult({ error: e.message })
    }
    setSending(false)
  }

  async function schedule() {
    if (!scheduledFor) return
    setSending(true)
    setResult(null)
    try {
      // Always send at 12:00 UTC (7am ET / 5am MT)
      const sendAt = new Date(scheduledFor + 'T12:00:00.000Z')
      const { error } = await supabase.from('issues').update({
        scheduled_for: sendAt.toISOString(),
        status: 'scheduled'
      }).eq('id', issue.id)
      if (error) throw error
      setResult({ scheduled: sendAt.toISOString() })
      setTimeout(onSent, 1500)
    } catch (e) {
      setResult({ error: e.message })
    }
    setSending(false)
  }

  async function cancelSchedule() {
    setSending(true)
    try {
      await supabase.from('issues').update({
        scheduled_for: null,
        status: 'draft'
      }).eq('id', issue.id)
      onSent()
    } catch (e) {
      setResult({ error: e.message })
    }
    setSending(false)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <h2 className="modal-title">Publish edition</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 'calc(0.72rem * 1.2)', color: 'var(--mid)', marginBottom: '0.5rem' }}>
          Subject: <strong style={{ color: 'var(--ink)' }}>{subject}</strong>
        </p>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 'calc(0.68rem * 1.2)', color: 'var(--mid)', marginBottom: '1.25rem' }}>
          {formatIssueDate(issue.send_date)}
        </p>

        {/* Scheduled status */}
        {isScheduled && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'rgba(94,128,251,0.08)', border: '1px solid rgba(94,128,251,0.3)',
            borderRadius: 3, padding: '0.75rem', marginBottom: '1.25rem'
          }}>
            <div>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 'calc(0.65rem * 1.2)', color: '#5E80FB', fontWeight: 600, letterSpacing: '0.08em' }}>
                SCHEDULED
              </p>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 'calc(0.7rem * 1.2)', color: 'var(--ink)', marginTop: '0.2rem' }}>
                {formatScheduledTime(issue.scheduled_for)}
              </p>
            </div>
            <button className="btn-secondary" onClick={cancelSchedule} disabled={sending}
              style={{ fontSize: 'calc(0.65rem * var(--scale))', color: '#c0392b', borderColor: '#c0392b' }}>
              Cancel
            </button>
          </div>
        )}

        {result && (
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 'calc(0.72rem * 1.2)',
            padding: '0.75rem', borderRadius: 3, marginBottom: '1rem',
            background: result.error ? 'rgba(192,57,43,0.08)' : 'rgba(94,128,251,0.08)',
            color: result.error ? '#c0392b' : '#5E80FB'
          }}>
            {result.error
              ? 'Error: ' + result.error
              : result.scheduled
                ? '✓ Scheduled for ' + formatScheduledTime(result.scheduled)
                : result.test
                  ? '✓ Test email sent to your inbox'
                  : `✓ Sent to ${result.sent} subscriber${result.sent !== 1 ? 's' : ''}`
            }
          </div>
        )}

        {/* Schedule section */}
        <div style={{ borderTop: '1px solid var(--rule)', paddingTop: '1rem', marginBottom: '1.25rem' }}>
          <label className="editor-label" style={{ display: 'block', marginBottom: '0.5rem' }}>
            {isScheduled ? 'Reschedule' : 'Schedule send'}
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input type="date" className="editor-input"
              value={scheduledFor}
              onChange={e => setScheduledFor(e.target.value)}
              style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 'calc(0.75rem * var(--scale))' }} />
            <button className="btn-secondary" onClick={schedule}
              disabled={sending || !scheduledFor} style={{ flexShrink: 0 }}>
              {isScheduled ? 'Reschedule' : 'Schedule'}
            </button>
          </div>
        </div>

        <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-secondary" onClick={() => send(true)} disabled={sending}>
            {sending ? 'Sending…' : 'Test'}
          </button>
          <button className="btn-primary" onClick={() => send(false)} disabled={sending}>
            {sending ? 'Sending…' : 'Send now'}
          </button>
        </div>
      </div>
    </div>
  )
}
